import { useEffect, useMemo, useState } from "react";
import { geoEquirectangular, geoPath } from "d3-geo";
import { feature } from "topojson-client";

// The iGaming licensing and operations hubs our verified reviews cover.
export const HUBS = [
  { name: "Gibraltar", lon: -5.35, lat: 36.14 },
  { name: "Malta", lon: 14.51, lat: 35.90 },
  { name: "Isle of Man", lon: -4.55, lat: 54.24 },
  { name: "Alderney", lon: -2.19, lat: 49.72 },
  { name: "Curaçao", lon: -68.99, lat: 12.17 },
  { name: "Antigua", lon: -61.85, lat: 17.06 },
  { name: "Kahnawake", lon: -73.68, lat: 45.42 },
  { name: "United Kingdom", lon: -0.13, lat: 51.51 },
  { name: "Sweden", lon: 18.06, lat: 59.33 },
  { name: "Estonia", lon: 24.75, lat: 59.44 },
  { name: "Bulgaria", lon: 23.32, lat: 42.70 },
  { name: "Romania", lon: 26.10, lat: 44.43 },
  { name: "Cyprus", lon: 33.36, lat: 35.17 },
  { name: "Philippines", lon: 121.05, lat: 14.60 },
  { name: "Costa Rica", lon: -84.09, lat: 9.93 },
  { name: "Panama", lon: -79.52, lat: 8.98 },
  { name: "Belize", lon: -88.19, lat: 17.19 },
  { name: "Colombia", lon: -74.07, lat: 4.71 },
  { name: "Mexico", lon: -99.13, lat: 19.43 },
  { name: "South Africa", lon: 28.05, lat: -26.20 },
  { name: "Georgia", lon: 44.79, lat: 41.72 },
  { name: "Vanuatu", lon: 168.32, lat: -17.73 },
];

const W = 1920, H = 960, M = 24;
const TOPO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

export function GlobalHubsMap({ onHubClick }) {
  const [countries, setCountries] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(TOPO_URL)
      .then((r) => r.json())
      .then((topo) => { if (active) setCountries(feature(topo, topo.objects.countries)); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const { paths, dots } = useMemo(() => {
    if (!countries) return { paths: [], dots: [] };
    const projection = geoEquirectangular().fitExtent([[M, M], [W - M, H - M]], countries);
    const path = geoPath(projection);
    const paths = countries.features.map((f, i) => ({ id: i, d: path(f) }));
    const dots = HUBS
      .map((h) => { const p = projection([h.lon, h.lat]); return p ? { name: h.name, x: p[0], y: p[1] } : null; })
      .filter(Boolean);
    return { paths, dots };
  }, [countries]);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      className="block h-full w-full"
      role="img"
      aria-label="World map highlighting the iGaming licensing hubs iGamingTruth covers"
      data-testid="hubs-map"
    >
      <rect width={W} height={H} fill="#FCFBF8" />
      {paths.map((p) => (
        <path key={p.id} d={p.d} fill="#3F8F6B" stroke="#FCFBF8" strokeWidth="0.6" />
      ))}
      {dots.map((d, i) => (
        <g key={i}>
          <title>{d.name}</title>
          <circle cx={d.x} cy={d.y} r="16" fill="none" stroke="#C1440E" strokeWidth="3" opacity="0.5" style={{ pointerEvents: "none" }}>
            <animate attributeName="r" values="14;30;14" dur="3s" begin={`${i * 0.15}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0;0.5" dur="3s" begin={`${i * 0.15}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={d.x} cy={d.y} r="12" fill="#C1440E" stroke="#FCFBF8" strokeWidth="2.5" style={{ pointerEvents: "none" }} />
          <circle cx={d.x} cy={d.y} r="20" fill="transparent" data-testid="hub-dot"
                  onClick={() => onHubClick && onHubClick(d.name)}
                  style={{ cursor: onHubClick ? "pointer" : "default" }} />
        </g>
      ))}
    </svg>
  );
}
